import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { getRequest } from "services/api";
import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import { buildQueryString } from "utils/queryHelpers";

const MealContext = createContext();

export const MealProvider = ({ children }) => {
    const [meals, setMeals] = useState({
        data: [],
        pagination: null,
        error: null,
        loading: false,
    });

    const [filterOptions, setFilterOptions] = useState({
        search: "",
        currentPage: 1,
    });

    // ✅ Gunakan useCallback untuk mencegah re-render tidak perlu
    const fetchMeals = useCallback(async (search = "", page = 1) => {
        setMeals((prev) => ({ ...prev, loading: true }));

        const queryString = buildQueryString({ search, page, limit: 20 });

        try {
            const response = await getRequest(`/meal${queryString}`);
            if (response.success) {
                setMeals({
                    data: response.data,
                    pagination: response.pagination,
                    error: null,
                    loading: false,
                });
            } else {
                throw new Error("Failed to fetch meals");
            }
        } catch (error) {
            setMeals((prev) => ({
                ...prev,
                error: error.message,
                loading: false,
            }));
        }
    }, []);

    // ✅ Gunakan useEffect untuk pemanggilan API hanya saat filterOptions berubah
    useEffect(() => {
        fetchMeals(filterOptions.search, filterOptions.currentPage);
    }, [fetchMeals, filterOptions]);

    // ✅ Handle perubahan pencarian dengan debounce
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            const searchValue = e.target.value.trim();
            if (searchValue !== filterOptions.search) {
                setFilterOptions((prev) => ({ ...prev, search: searchValue }));
            }
        }
    };

    // ✅ Handle perubahan halaman
    const handlePageChange = (page) => {
        setFilterOptions((prev) => ({ ...prev, currentPage: page }));
    };

    const Pagination = usePaginationWithComponent({
        totalPages: meals.pagination?.last_page || 1,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5,
    });

    return (
        <MealContext.Provider
            value={{
                meals,
                handleSearch,
                filterOptions,
                refetch: () =>
                    fetchMeals(filterOptions.search, filterOptions.currentPage),
                Pagination,
                fetchOptions: async (inputValue) => {
                    const response = await getRequest(
                        `/meal?search=${inputValue}`
                    );
                    return response.success
                        ? response.data.map((meal) => ({
                                value: meal.id,
                                label: meal.name,
                            }))
                        : [];
                },
            }}
        >
            {children}
        </MealContext.Provider>
    );
};

export const useMeal = () => {
    const context = useContext(MealContext);
    if (!context) {
        throw new Error(
            "useMeal must be used within MealProvider"
        );
    }
    return context;
};
